'use client'

import * as styles from '@shared/styles/suche.css'
import Link from 'next/link'
import { Row, Stack } from '@christiankaindl/lyts'
import Heading from '@shared/components/Heading/Heading'
import { card } from '@shared/styles/theme.css'
import Text from '@shared/components/Text/Text'
import { formatDate, getDecisionUrl, getUrl } from '@shared/lib/utils'
import { BookMarked as LawIcon } from 'lucide-react'
import { Scale, SparklesIcon } from 'lucide-react'
import { type IDecision } from '@shared/lib/norms.types'
import { Fragment } from 'react'
import type { DocumentSearchResponse } from '@shared/lib/search.server'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shared/components/Tooltip/Tooltip'
import { lawTypeMappings } from '@shared/lib/search'

export interface RechtssatzNorm {
  normId?: string
  abbreviation?: string
  type: string
  /** The full string identifier. E.g. "StPO §281 Abs1 Z3" */
  norm: string
  enumeration?: string
  abbrNr?: number
  title?: string
}

type DocumentGroupResults = NonNullable<
  DocumentSearchResponse['grouped_hits']
>[0]

interface ResultProps {
  result: DocumentGroupResults
  // resultNb?: number
  // sendEvent: SendEventForHits | undefined
}

/**
 * Displays a search result.
 *
 * Include the `sendEvent` prop to enable Algolia Insights.
 */
export function Result({ result }: ResultProps) {
  if (result.hits.length === 0) {
    // We had cases where Typesense would return "found: 20" and then an empty `hits` array...
    return null
  }
  const documentType = result.hits[0]?.document.documentType
  if (documentType === 'decision') {
    return <EntscheidungResult result={result} />
  } else if (documentType === 'rechtssatz') {
    return <RechtssatzResult result={result} />
  } else {
    return <LawResult result={result} />
  }
}

export function LawResult({ result }: { result: DocumentGroupResults }) {
  const isFullNormMatch = false
  // const isFullNormMatch = result._highlightResult?.abbreviation?.matchLevel === 'full' && resultNb === 0

  // console.log('here 2?')

  // if (!result.hits) {
  //   return (
  //     <li className={styles.resultContainer}>
  //       <Row asChild gap='15px' className={styles.resultItem}>
  //         <Link href={path}>
  //           <div
  //             style={{
  //               height: 42,
  //               width: 42,
  //               display: 'flex',
  //               justifyContent: 'center',
  //               alignItems: 'center',
  //               backgroundColor: 'rgba(228, 177, 95, 0.2)',
  //               borderRadius: 99,
  //               marginLeft: -6,
  //               marginTop: -6,
  //               marginBottom: -6,
  //               color: '#A48757',
  //               flexShrink: 0,
  //             }}
  //           >
  //             <LawIcon width={20} />
  //           </div>
  //           <Stack gap={0}>
  //             <Text style={{ flexGrow: 1, lineHeight: 1.3 }} weight={600} size={isFullNormMatch ? 'large' : '1.075em'} maxLines={2}>
  //               {result.title}
  //             </Text>
  //             <Text style={{ marginTop: 0 }} color='secondary'>{result.abbreviation && `${result.abbreviation} · `}{mapping[result.lawType?.toLowerCase()] ?? result.lawType}</Text>
  //           </Stack>
  //         </Link>
  //       </Row>
  //     </li>
  //   )
  // }

  return (
    <Stack gap='1px' className={card} style={{ padding: 6 }}>
      {result.hits &&
        result.hits.length > 0 &&
        result.hits?.map(({ document: hit, highlight }, index) => {
          if (!hit.lawType) return null

          // console.log('here 1?', hit.lawType, hit.abbreviation, hit.normId, hit.documentType)
          const path = getUrl(hit.lawType, {
            norm: hit.abbreviation ?? hit.normId,
            normId: hit.normId,
          })

          if (hit.documentType === 'law') {
            // Full norm match
            return (
              <Fragment key={hit.id}>
                <Row asChild gap='15px' className={styles.resultItem}>
                  <Link
                    href={path}
                    // onClick={() => {
                    //   sendEvent?.('click', hit, 'Hit Clicked')
                    // }}
                  >
                    <div
                      style={{
                        height: 42,
                        width: 42,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(228, 177, 95, 0.2)',
                        borderRadius: 99,
                        marginLeft: -6,
                        marginTop: -6,
                        marginBottom: -6,
                        color: '#A48757',
                        flexShrink: 0,
                      }}
                    >
                      <LawIcon width={20} />
                    </div>
                    <Stack gap={0}>
                      <Text
                        style={{ flexGrow: 1, lineHeight: 1.3 }}
                        weight={600}
                        size={isFullNormMatch ? 'large' : '1.075em'}
                        maxLines={2}
                      >
                        {/* Indexed law documents carry the § 0 clause title in
                            `title` (e.g. "Vorwort/Präambel"); the law's name is
                            in `lawTitle`. */}
                        {hit.lawTitle ?? hit.title}
                      </Text>
                      <Text style={{ marginTop: 0 }} color='secondary'>
                        {hit.citation &&
                          hit.citation !== hit.abbreviation &&
                          `${hit.citation} · `}
                        {hit.abbreviation && `${hit.abbreviation} · `}
                        {lawTypeMappings[hit.lawType.toLowerCase()] ??
                          hit.lawType}
                        {hit.state && ` · ${hit.state}`}
                      </Text>
                    </Stack>
                  </Link>
                </Row>
                {index === 0 && result.hits && result.hits.length > 1 && (
                  <>
                    <hr style={{ margin: '6px -6px 6px 15px' }} />
                  </>
                )}
              </Fragment>
            )
          }

          const isClauseFullMatch = false
          if (!hit.enumeration) return null
          // Build via getUrl so the enumeration lands in the path *before*
          // the `?id=` query param — appending to `path` would put it after.
          const clausePath = getUrl(hit.lawType, {
            norm: hit.abbreviation ?? hit.normId,
            normId: hit.normId,
            enumeration: hit.enumeration,
          })
          // console.log('hit', hit)
          return (
            // Clause results
            <Fragment key={hit.id}>
              {index === 0 && hit.documentType === 'clause' && (
                // Small "banner" showing the law abbreviation and title for clause results
                <Row asChild gap='6px' style={{ display: 'inline-flex' }}>
                  <Link
                    href={path}
                    style={{
                      padding: '9px 15px 6px',
                      color: '#A48757',
                    }}
                  >
                    <LawIcon width={16} />
                    <Text
                      color='currentColor'
                      size='0.85rem'
                      weight={550}
                      maxLines={2}
                    >
                      {hit.citation && hit.citation !== hit.abbreviation
                        ? `${hit.citation} · `
                        : ''}
                      {hit.abbreviation ? `${hit.abbreviation} · ` : ''}
                      {hit.lawTitle}
                    </Text>
                  </Link>
                </Row>
              )}
              <Stack
                gap={index === 0 ? '6px' : '3px'}
                asChild
                className={styles.resultItem}
                title={hit.title}
              >
                <Link
                  href={clausePath}
                  onClick={() => {
                    // sendEvent?.('click', hit, 'Hit Clicked')
                  }}
                >
                  <Text
                    maxLines={2}
                    style={{ lineHeight: 1.4 }}
                    weight={isClauseFullMatch ? 600 : 500}
                    size={isClauseFullMatch ? 'large' : undefined}
                  >
                    {hit.enumeration}&ensp;{hit.title}
                    {/* {hit.citation} */}
                    {/* {hit.enumeration && <Snippet style={{ display: 'inline-block', paddingRight: 9 }} highlightedTagName='mark' attribute='enumeration' hit={hit} />} */}
                    {/* {hit.clause?.enumeration && <Snippet style={{ display: 'inline-block', paddingRight: 9 }} highlightedTagName='mark' attribute='clause.enumeration' hit={hit} />} */}
                    {/* {hit.title && (<Snippet highlightedTagName='mark' attribute='title' hit={hit} />)} */}
                    {/* {hit.clause?.title && (<Snippet highlightedTagName='mark' attribute='clause.title' hit={hit} />)} */}
                  </Text>

                  {highlight.content?.snippet && (
                    <Text color='secondary'>
                      …
                      <span
                        dangerouslySetInnerHTML={{
                          __html: highlight.content.snippet,
                        }}
                      />
                      …
                    </Text>
                  )}
                  {!highlight.content?.snippet && (
                    <Text color='secondary'>{hit.snippet}</Text>
                  )}
                  {/* <Text hyphens weight={300} style={{ opacity: isClauseFullMatch ? 1 : 0.85, lineHeight: 1.4 }} maxLines={isClauseFullMatch ? undefined : 2}>
                  {hit.content}
                </Text> */}
                </Link>
              </Stack>
              {isClauseFullMatch && result.hits && result.hits.length > 1 && (
                <hr style={{ margin: '6px -6px 6px 15px' }} />
              )}
            </Fragment>
          )
        })}
    </Stack>
  )
}

export function EntscheidungResult({
  result: res,
}: {
  result: DocumentGroupResults
}) {
  const result = res.hits[0]?.document
  const highlight = res.hits[0]?.highlight
  if (!result) return null

  const path = getDecisionUrl(result as unknown as IDecision)

  return (
    <Stack
      gap='1px'
      className={`${card} ${!result.normId ? styles.disabled : ''}`}
      style={{ padding: 6, position: 'relative' }}
      title={!result.normId ? 'Entscheidung nicht verfügbar' : undefined}
    >
      <Stack asChild gap='9px' className={styles.resultItem}>
        <Link href={path} aria-disabled={!path}>
          <Row gap='15px'>
            <div
              style={{
                height: 42,
                width: 42,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(228, 177, 95, 0.2)',
                borderRadius: 99,
                marginLeft: -6,
                marginTop: -6,
                marginBottom: -6,
                color: '#A48757',
                flexShrink: 0,
              }}
            >
              <Scale width={20} />
            </div>
            <Stack gap={0}>
              <Heading h={3} style={{ flexGrow: 1 }}>
                {result.title ?? result.caseNumber}—
                {[result.institution].flat().join(', ')} Entscheidung
              </Heading>
              <Row gap={0.5} wrap>
                {result.title && (
                  <span style={{ opacity: 0.55 }}>{result.caseNumber}</span>
                )}
                <span style={{ opacity: 0.55 }}>
                  {formatDate(new Date(result.decisionDate).toISOString())}
                </span>
              </Row>
            </Stack>
          </Row>
          {highlight?.content?.snippet && (
            <Text color='secondary'>
              …
              <span
                dangerouslySetInnerHTML={{ __html: highlight.content.snippet }}
              />
              …
            </Text>
          )}
          {!highlight?.content?.snippet && (
            <Text color='secondary'>{result.snippet}</Text>
          )}
        </Link>
      </Stack>
      {result.hasSummary && result.documentType !== 'rechtssatz' && ( // for rechtssaetze, we have a summary available, but it is not relevant as the rechtssatz is short anyways
        <div style={{ position: 'absolute', top: 6, right: 6, padding: 6 }}>
          <Tooltip>
            <TooltipTrigger>
              <SparklesIcon color='#A48757' size={20} />
            </TooltipTrigger>
            <TooltipContent>Codara AI Summary verfügbar</TooltipContent>
          </Tooltip>
        </div>
      )}
      {/* {result._snippetResult?.content?.map?.((item, index) => {
        if (item.content.matchLevel === 'none') {
          return null
        }

        return (
          // eslint-disable-next-line
          <Link key={index} href={`${path}#${item.title.value.toLowerCase().replaceAll(' ', '-')}`} className={styles.resultItem}>
            <Text weight={300} style={{ opacity: 0.85, lineHeight: 1.4 }}>
              <b style={{ fontWeight: 600 }}>{item.title.value}</b><br />
              <span dangerouslySetInnerHTML={{ __html: item.content.value }} />
            </Text>
          </Link>
        )
      })} */}
    </Stack>
  )
}

export function RechtssatzResult({
  result: res,
}: {
  result: DocumentGroupResults
}) {
  const result = res.hits[0]?.document
  const highlight = res.hits[0]?.highlight

  const path = getDecisionUrl(result as unknown as IDecision)

  return (
    <Stack
      gap='1px'
      style={{ padding: 6 }}
      className={card}
      onClick={() => {
        // // @ts-expect-error IEntscheidungResult type is wrong
        // sendEvent?.('click', result, 'Hit Clicked')
      }}
    >
      <Stack asChild gap='9px' className={styles.resultItem}>
        <Link href={path}>
          <Row gap='15px'>
            <div
              style={{
                height: 42,
                width: 42,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(228, 177, 95, 0.2)',
                borderRadius: 99,
                marginLeft: -6,
                marginTop: -6,
                marginBottom: -6,
                color: '#A48757',
                flexShrink: 0,
              }}
            >
              <Scale width={20} />
            </div>
            <Stack gap={0}>
              <Heading h={3} style={{ flexGrow: 1 }}>
                {result?.caseNumber}—{[result?.institution].flat().join(', ')}{' '}
                Rechtssatz
              </Heading>
              <Row gap={0.5} wrap>
                <span style={{ opacity: 0.55 }}>
                  {formatDate(
                    new Date(result?.decisionDate as number).toISOString(),
                  )}
                </span>
              </Row>
            </Stack>
          </Row>
          {highlight?.content?.snippet && (
            <Text color='secondary'>
              <span
                dangerouslySetInnerHTML={{ __html: highlight.content.snippet }}
              />
              …
            </Text>
          )}
          {!highlight?.content?.snippet && (
            <Text color='secondary'>{result?.snippet}</Text>
          )}
        </Link>
      </Stack>
    </Stack>
  )
}
